import React from 'react'
import { useAudioPlayer, useAudioPosition } from 'react-use-audio-player'
import { Howler } from 'howler'
import { ResizeObserver } from 'resize-observer'
import { useMoteListenActivityMutation, useTrackMutation } from '@mote/api'
import { isNumeric, useEventListener } from '@mote/helpers'
import {
  isMacOS,
  isMobile,
  isWindows,
  preventBubblingEvent
} from '@mote/helpers/browser'
import { KeyboardKey, TRACK_EVENT_NAMES, noop } from '@mote/helpers/constants'
import { getAudioData } from '@mote/helpers/media'

const SEEK_SLOW_SECONDS = 2
const SEEK_FAST_SECONDS = 5
const RATE_OPTIONS = [0.8, 1, 1.2, 1.5]
const DEFAULT_PLAYBACK_CALLBACKS = {
  onPlay: noop,
  onPause: noop,
  onEnd: noop,
  onReplay: noop,
  onDelete: noop,
  onFullTranscript: noop
}

export const MotePlayerContext = React.createContext({})

export const MotePlayerProvider = ({
  autoFocus = true,
  isRichCard = false,
  isLandingPage = false,
  isEmbed = false,
  audioSource,
  theme = {},
  moteId,
  moteCreatedByUserId,
  children
}) => {
  const { mutate: track } = useTrackMutation()
  const { mutate: listenActivity } = useMoteListenActivityMutation({
    moteId
  })
  const {
    play,
    pause,
    player,
    playing,
    volume,
    loading,
    ready,
    ...restAudioPlayer
  } = useAudioPlayer()
  const { position, duration, seek, ...restAudioPosition } = useAudioPosition()
  const [playbackCallbacks, setPlaybackCallbacks] = React.useState(
    DEFAULT_PLAYBACK_CALLBACKS
  )
  const [error, setError] = React.useState(null)
  const [audioData, setAudioData] = React.useState(null)
  const [active, setActive] = React.useState(false)
  const [ended, setEnded] = React.useState(false)
  const [paused, setPaused] = React.useState(!playing)
  const [muted, setMuted] = React.useState(false)
  const [localVolume, setLocalVolume] = React.useState(100)
  const [preMuteValue, setPreMuteValue] = React.useState(100)
  const [showFullTranscript, setShowFullTranscript] = React.useState(false)
  const [playbackRate, setPlaybackRate] = React.useState(RATE_OPTIONS[1])
  const [playerDimensions, setPlayerDimensions] = React.useState(null)
  const playedOnceRef = React.useRef(false)
  const prevPlayingState = React.useRef(null)
  const playerRef = React.useRef(null)
  const transcriptRef = React.useRef(null)
  const isPlayerFocused = React.useRef(!!autoFocus)
  const currentRateIndexRef = React.useRef(1)
  const transcriptCurrentScrollRef = React.useRef(0)
  const isReady = React.useMemo(() => !loading && ready, [loading, ready])

  useEventListener(
    'focus',
    () => {
      isPlayerFocused.current = true
    },
    playerRef
  )

  useEventListener(
    'blur',
    () => {
      isPlayerFocused.current = false
    },
    playerRef
  )

  React.useEffect(() => {
    if (autoFocus) playerRef.current?.focus()
  }, [])

  const handleTracking = React.useCallback(
    (event, payload = {}) => {
      let embedPayload = {}
      let listeningContext

      if (isLandingPage) listeningContext = 'landing-page'
      else if (isEmbed) listeningContext = 'embed'
      else listeningContext = 'extension'

      if (isEmbed) {
        let { href, ...location } = window.location

        try {
          href = window.parent.location.href
          location = window.parent.location
        } catch {
          /* do nothing */
        }

        embedPayload = {
          page_context: href,
          application_domain: location.hostname,
          application_domain_path_prefix: `${location.hostname}/${
            location.pathname.replace('/', '')?.split('/')?.[0]
          }`
        }
      }

      track({
        variables: {
          event,
          ...payload,
          listening_context: listeningContext,
          author_asset_id: moteId,
          author_user_id: moteCreatedByUserId,
          ...embedPayload
        }
      })
    },
    [track, isEmbed, isLandingPage, moteId, moteCreatedByUserId]
  )

  const onPlayerResize = React.useCallback(entries => {
    setPlayerDimensions(entries[0].contentRect)
  }, [])

  React.useEffect(() => {
    if (!isRichCard) return
    if (!playerRef.current) return

    const resizeObserver = new ResizeObserver(onPlayerResize)

    resizeObserver.observe(playerRef.current)
  }, [isRichCard, onPlayerResize])

  const handleOnAudioLoad = React.useCallback(async () => {
    if (!audioSource || audioData) return

    const soundExists = Howler._howls.find(howl => howl._src === audioSource)

    if (!soundExists) return

    setAudioData(await getAudioData(audioSource, Howler.audioBuffer))
  }, [audioSource, audioData])

  const addPlaybackCallbacks = React.useCallback((functions = {}) => {
    if (Object.keys(functions).length === 0) return

    setPlaybackCallbacks(prev => ({ ...prev, ...functions }))
  }, [])

  const getAudioPosition = React.useCallback(
    () => (position ? Math.round(position * 10) / 10 : 0),
    [position]
  )

  const handleOnLoadError = React.useCallback(error => {
    setError(error)
  }, [])

  const handleOnEnd = React.useCallback(() => {
    playbackCallbacks.onEnd?.()
    handleTracking(TRACK_EVENT_NAMES.mote.playback.end)
  }, [playbackCallbacks, handleTracking])

  React.useEffect(() => {
    if (!player) return
    player.on('end', handleOnEnd)

    return () => {
      player.off('end', handleOnEnd)
    }
  }, [player, handleOnEnd])

  const toggleFullTranscript = React.useCallback(() => {
    setShowFullTranscript(prev => {
      const state = !prev

      handleTracking(
        `${TRACK_EVENT_NAMES.mote.action.fullTranscript} (${
          state ? 'Show' : 'Hide'
        })`
      )

      return state
    })
    transcriptCurrentScrollRef.current = 0
    playbackCallbacks.onFullTranscript?.()
  }, [playbackCallbacks, handleTracking])

  const handlePlaybackRateChange = React.useCallback(
    (newRate, event, index) => {
      if (!player) return
      if (!isRichCard) return
      if (!isNumeric(newRate)) return false

      const rate = +newRate

      player.rate(rate)
      setPlaybackRate(rate)
      currentRateIndexRef.current = index

      handleTracking(TRACK_EVENT_NAMES.mote.playback.speed, {
        speed: rate
      })

      return true
    },
    [isRichCard, player, handleTracking]
  )

  const handlePlay = React.useCallback(() => {
    play()
    setPaused(false)
    playbackCallbacks.onPlay?.(getAudioPosition())

    handleTracking(TRACK_EVENT_NAMES.mote.playback.play)

    if (!playedOnceRef.current) {
      listenActivity().catch(console.error)
      playedOnceRef.current = true
    }
  }, [
    play,
    playbackCallbacks,
    getAudioPosition,
    handleTracking,
    listenActivity
  ])

  const handlePause = React.useCallback(() => {
    pause()
    setPaused(true)
    playbackCallbacks.onPause?.(getAudioPosition())

    handleTracking(TRACK_EVENT_NAMES.mote.playback.pause)
  }, [pause, playbackCallbacks, getAudioPosition, handleTracking])

  const handleReplay = React.useCallback(() => {
    handlePlay()
    playbackCallbacks.onReplay?.()

    handleTracking(TRACK_EVENT_NAMES.mote.playback.replay)
  }, [handlePlay, playbackCallbacks, handleTracking])

  const handleOnVolumeChange = React.useCallback(
    value => {
      if (player) player.mute(!value)

      setMuted(!value)
      setLocalVolume(value)
      volume(value / 100)
    },
    [player, volume]
  )

  const increaseVolume = React.useCallback(() => {
    if (localVolume >= 100) return

    handleOnVolumeChange(localVolume + 10)
  }, [handleOnVolumeChange, localVolume])

  const decreaseVolume = React.useCallback(() => {
    if (localVolume <= 0) return

    handleOnVolumeChange(localVolume - 10)
  }, [handleOnVolumeChange, localVolume])

  const toggleMute = React.useCallback(() => {
    if (!player) return
    if (!muted && localVolume) setPreMuteValue(localVolume)
    setMuted(!muted)
    handleOnVolumeChange(!muted ? 0 : preMuteValue)
    player.mute(!muted)
  }, [muted, localVolume, preMuteValue, player, handleOnVolumeChange])

  const handleOnDelete = React.useCallback(() => {
    playbackCallbacks.onDelete?.()

    handleTracking(TRACK_EVENT_NAMES.mote.action.delete)
  }, [playbackCallbacks, handleTracking])

  const handleOnRewind = React.useCallback(
    (speed = SEEK_FAST_SECONDS) => {
      seek(Math.max(0, position - speed))

      handleTracking(TRACK_EVENT_NAMES.mote.playback.rewind)
    },
    [seek, position, handleTracking]
  )

  const handleOnForward = React.useCallback(
    (speed = SEEK_FAST_SECONDS) => {
      seek(Math.min(duration, position + speed))

      handleTracking(TRACK_EVENT_NAMES.mote.playback.forward)
    },
    [seek, position, duration, handleTracking]
  )

  const handleScrubberValueChange = React.useCallback(
    value => {
      prevPlayingState.current = playing
      if (playing) {
        handlePause()
      }
      seek(value)
    },
    [handlePause, seek, playing]
  )

  const handleScrubberValueChangeEnd = React.useCallback(() => {
    if (prevPlayingState.current) {
      handlePlay()
    }
  }, [handlePlay])

  const handleOnPlayerKeydown = React.useCallback(
    event => {
      if (!isLandingPage && document.activeElement !== playerRef.current) return
      if (isRichCard) {
        if (!active && !ended) {
          switch (event.keyCode) {
            case KeyboardKey.Space:
              preventBubblingEvent(event)
              handlePlay()
              break
          }
          return
        } else if (!active && ended) {
          switch (event.keyCode) {
            case KeyboardKey.R:
              preventBubblingEvent(event)
              handleReplay()
              break
          }

          return
        }
      }

      const isCtrlKey =
        (isWindows() && event.ctrlKey) || (isMacOS() && event.metaKey)

      switch (event.keyCode) {
        case KeyboardKey.Space:
        case KeyboardKey.K:
          preventBubblingEvent(event)
          if (playing) {
            handlePause()
            break
          }
          handlePlay()
          break
        case KeyboardKey.M:
          preventBubblingEvent(event)
          toggleMute()
          break
        case KeyboardKey.UpArrow:
          preventBubblingEvent(event)
          increaseVolume()
          break
        case KeyboardKey.DownArrow:
          preventBubblingEvent(event)
          decreaseVolume()
          break
        case KeyboardKey.J:
          preventBubblingEvent(event)
          handleOnRewind()
          break
        case KeyboardKey.LeftArrow:
          preventBubblingEvent(event)
          handleOnRewind(SEEK_SLOW_SECONDS)
          break
        case KeyboardKey.L:
          preventBubblingEvent(event)
          handleOnForward()
          break
        case KeyboardKey.RightArrow:
          preventBubblingEvent(event)
          handleOnForward(SEEK_SLOW_SECONDS)
          break
        case KeyboardKey.D:
          preventBubblingEvent(event)
          handleOnDelete()
          break
        case KeyboardKey.C:
          if (!isRichCard) return
          if (isCtrlKey) return

          preventBubblingEvent(event)
          toggleFullTranscript()
          break
        case KeyboardKey.Z:
          if (!isRichCard) return
          if (!showFullTranscript) return
          if (isCtrlKey) return
          preventBubblingEvent(event)
          transcriptCurrentScrollRef.current -= 25

          if (transcriptCurrentScrollRef.current < 0)
            transcriptCurrentScrollRef.current = 0

          transcriptRef.current?.scroll({
            top: transcriptCurrentScrollRef.current,
            behavior: 'smooth'
          })
          break
        case KeyboardKey.X: {
          if (!isRichCard) return
          if (!showFullTranscript) return
          if (isCtrlKey) return
          preventBubblingEvent(event)

          const maxScroll = transcriptRef.current?.scrollHeight
          const scrollWithOffset =
            transcriptCurrentScrollRef.current +
            25 +
            transcriptRef.current?.offsetHeight

          const shouldAddScroll = scrollWithOffset <= maxScroll

          if (shouldAddScroll) transcriptCurrentScrollRef.current += 25

          transcriptRef.current?.scroll({
            top: shouldAddScroll
              ? transcriptCurrentScrollRef.current
              : maxScroll,
            behavior: 'smooth'
          })

          break
        }

        case KeyboardKey.Comma: {
          if (!isRichCard) return

          preventBubblingEvent(event)

          if (currentRateIndexRef.current === 0) return
          let prevRateIndex = currentRateIndexRef.current - 1

          if (prevRateIndex < 0) prevRateIndex = 0

          handlePlaybackRateChange(
            RATE_OPTIONS[prevRateIndex],
            null,
            prevRateIndex
          )
          break
        }
        case KeyboardKey.Period: {
          if (!isRichCard) return

          preventBubblingEvent(event)

          if (currentRateIndexRef.current === RATE_OPTIONS.length) return
          let nextRateIndex = currentRateIndexRef.current + 1

          if (nextRateIndex > RATE_OPTIONS.length)
            nextRateIndex = RATE_OPTIONS.length

          handlePlaybackRateChange(
            RATE_OPTIONS[nextRateIndex],
            null,
            nextRateIndex
          )
          break
        }
      }
    },
    [
      active,
      ended,
      isLandingPage,
      isRichCard,
      showFullTranscript,
      playing,
      handlePlay,
      handlePause,
      handleReplay,
      handleOnForward,
      handleOnRewind,
      handleOnDelete,
      handlePlaybackRateChange,
      decreaseVolume,
      increaseVolume,
      toggleMute,
      toggleFullTranscript
    ]
  )

  const handleOnPlayerKeyUp = React.useCallback(
    event => {
      if (!isLandingPage && document.activeElement !== playerRef.current) return

      if (isRichCard) {
        if (!active && !ended) {
          switch (event.keyCode) {
            case KeyboardKey.Space:
              event.preventDefault()
              break
          }
          return
        } else if (!active && ended) {
          switch (event.keyCode) {
            case KeyboardKey.R:
              event.preventDefault()
              break
          }

          return
        }
      }

      const isCtrlKey =
        (isWindows() && event.ctrlKey) || (isMacOS() && event.metaKey)

      switch (event.keyCode) {
        case KeyboardKey.Space:
        case KeyboardKey.M:
        case KeyboardKey.J:
        case KeyboardKey.K:
        case KeyboardKey.L:
        case KeyboardKey.D:
          event.preventDefault()
          break
        case KeyboardKey.Z:
        case KeyboardKey.X:
          if (!isRichCard) return
          if (!showFullTranscript) return
          if (isCtrlKey) return

          event.preventDefault()
          break
        case KeyboardKey.C:
          if (isCtrlKey) return

          event.preventDefault()
          break
        case KeyboardKey.Comma:
        case KeyboardKey.Period:
          if (!isRichCard) return

          event.preventDefault()
          break
      }
    },
    [active, ended, isRichCard, isLandingPage, showFullTranscript]
  )

  const handleOnPlayerClick = React.useCallback(() => {
    playerRef.current?.focus()
  }, [])

  const playerProps = React.useMemo(() => {
    let props = {
      ref: playerRef,
      tabIndex: 0,
      className: 'mote-card',
      role: 'region',
      'aria-label': 'Mote Player'
    }

    if (!isLandingPage && isReady) {
      props = {
        ...props,
        onMouseDown: handleOnPlayerClick,
        onKeyDown: handleOnPlayerKeydown,
        onKeyUp: handleOnPlayerKeyUp
      }
    }

    return props
  }, [
    isLandingPage,
    isReady,
    handleOnPlayerKeydown,
    handleOnPlayerKeyUp,
    handleOnPlayerClick
  ])

  useEventListener('keydown', handleOnPlayerKeydown, null, {
    enabled: isReady && isLandingPage
  })
  useEventListener('keyup', handleOnPlayerKeyUp, null, {
    enabled: isReady && isLandingPage
  })

  const providerValues = React.useMemo(() => {
    let values = {
      play: handlePlay,
      pause: handlePause,
      player,
      playing,
      paused,
      volume,
      loading,
      ready,
      ...restAudioPlayer,
      position,
      duration,
      seek,
      ...restAudioPosition,
      onScrubberValueChange: handleScrubberValueChange,
      onScrubberValueChangeEnd: handleScrubberValueChangeEnd,
      onVolumeChange: handleOnVolumeChange,
      onRewind: handleOnRewind,
      onForward: handleOnForward,
      onDelete: handleOnDelete,
      onMute: setMuted,
      toggleMute,
      getAudioPosition,
      muted,
      internalVolume: localVolume,
      preMuteValue,
      increaseVolume,
      decreaseVolume,
      playerProps,
      addPlaybackCallbacks,
      error,
      isReady,
      handleOnAudioLoad,
      handleOnLoadError,
      handleTracking
    }

    if (isRichCard) {
      values = {
        ...values,

        replay: handleReplay,
        onPlaybackRateChange: handlePlaybackRateChange,
        toggleFullTranscript,
        playbackRate,
        playbackRateOptions: RATE_OPTIONS,
        showFullTranscript,
        moteId,
        theme,
        playerDimensions,
        active,
        ended,
        setActive,
        setEnded,
        transcriptRef,
        audioSource,
        audioData
      }
    }

    return values
  }, [
    loading,
    ready,
    isReady,
    active,
    ended,
    isRichCard,
    handlePlay,
    handlePause,
    handleReplay,
    player,
    playing,
    paused,
    volume,
    restAudioPlayer,
    position,
    duration,
    seek,
    restAudioPosition,
    handleScrubberValueChange,
    handleScrubberValueChangeEnd,
    handleOnVolumeChange,
    handleOnRewind,
    handleOnForward,
    handleOnDelete,
    setMuted,
    handlePlaybackRateChange,
    toggleMute,
    toggleFullTranscript,
    getAudioPosition,
    playbackRate,
    muted,
    localVolume,
    preMuteValue,
    increaseVolume,
    decreaseVolume,
    playerProps,
    addPlaybackCallbacks,
    showFullTranscript,
    moteId,
    theme,
    playerDimensions,
    setActive,
    setEnded,
    audioData,
    audioSource,
    error,
    handleOnAudioLoad,
    handleOnLoadError,
    handleTracking
  ])

  return (
    <MotePlayerContext.Provider value={providerValues}>
      {children}
    </MotePlayerContext.Provider>
  )
}

export const useMotePlayer = (options = {}) => {
  const context = React.useContext(MotePlayerContext)

  const {
    init = false,
    highRefreshRate = true,
    src = '',
    html5 = true,
    preload = true,
    format = ['mp3'],
    autoplay = false,
    initialPercentagePosition,
    playbackCallbacks = {},
    isStandalone = true,
    isRichCard = false
  } = React.useMemo(() => options || {}, [options])

  const audioPlayerConfig = React.useMemo(
    () =>
      init
        ? {
            src,
            preload,
            html5: isRichCard ? html5 : isMobile(),
            format,
            autoplay,
            onload: context.handleOnAudioLoad,
            onloaderror: context.handleOnLoadError
          }
        : {},
    [init, src, preload, html5, isRichCard, format, autoplay]
  )

  const audioPositionConfig = React.useMemo(
    () =>
      init
        ? {
            highRefreshRate
          }
        : {},
    [init, highRefreshRate]
  )

  useAudioPlayer(audioPlayerConfig)
  const { seek, duration } = useAudioPosition(audioPositionConfig)

  if (init && initialPercentagePosition) {
    seek((initialPercentagePosition * duration) / 100)
  }

  React.useEffect(() => {
    if (!isStandalone) return
    if (!init) return

    context.addPlaybackCallbacks(playbackCallbacks)
  }, [isStandalone, init, playbackCallbacks])

  if (context === null) {
    throw new Error(
      'Attempted to call useMotePlayer outside the MoteCard component.'
    )
  }

  return context
}
